import { Shield } from "lucide-react";
import { Card, CardContent, IconChip } from "@/components/ui/card";

// Sobriety time reads as quiet dignity — Shield icon, plain text, "one day at
// a time". Never a scoreboard. Relapse reset is a calm underlined text link.
interface Breakdown {
  y: number;
  m: number;
  w: number;
  d: number;
  totalDays: number;
  totalHours: number;
}

function calc(start: Date, now = new Date()): Breakdown {
  const ms = now.getTime() - start.getTime();
  const day = 86_400_000;
  const totalDays = Math.floor(ms / day);
  const totalHours = Math.floor(ms / 3_600_000);

  let y = now.getFullYear() - start.getFullYear();
  const cursor = new Date(start);
  cursor.setFullYear(start.getFullYear() + y);
  if (cursor > now) {
    y--;
    cursor.setFullYear(cursor.getFullYear() - 1);
  }
  let m = 0;
  // Advance whole months without overshooting today.
  for (;;) {
    const next = new Date(cursor);
    next.setMonth(next.getMonth() + 1);
    if (next > now) break;
    cursor.setTime(next.getTime());
    m++;
  }
  const rest = Math.floor((now.getTime() - cursor.getTime()) / day);
  const w = Math.floor(rest / 7);
  const d = rest % 7;
  return { y, m, w, d, totalDays, totalHours };
}

function fmt(c: Breakdown): string {
  const parts: string[] = [];
  if (c.y) parts.push(`${c.y} ${c.y === 1 ? "Year" : "Years"}`);
  if (c.m) parts.push(`${c.m} ${c.m === 1 ? "Month" : "Months"}`);
  if (c.w) parts.push(`${c.w} ${c.w === 1 ? "Week" : "Weeks"}`);
  if (c.d || !parts.length) parts.push(`${c.d} ${c.d === 1 ? "Day" : "Days"}`);
  return parts.join(", ");
}

export function SobrietyCard({
  startDate,
  onReset,
  className,
  /**
   * The heading — "Sobriety Time", "Clean Time", "Abstinence Time", or
   * "Recovery Time" when the app is deliberately choosing no word
   * (Fellowships PRD §10.4). Defaults to the AA-era wording so existing
   * callers keep working.
   */
  label = "Sobriety Time",
}: {
  startDate: string | Date;
  onReset?: () => void;
  className?: string;
  label?: string;
}) {
  const start =
    typeof startDate === "string"
      ? new Date(`${startDate}T00:00:00`)
      : startDate;
  const c = calc(start);
  const startedStr = start.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <Card className={className}>
      <CardContent className="flex items-start gap-3">
        <IconChip icon={Shield} tint="primary" />
        <div className="min-w-0 flex-1">
          <p className="mb-1 text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
            {label}
          </p>
          <p className="text-lg font-bold leading-tight text-foreground">
            {fmt(c)}
          </p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {c.totalDays.toLocaleString()} Days ·{" "}
            {c.totalHours.toLocaleString()} Hours
          </p>
          <div className="mt-2 flex items-center justify-between gap-2">
            <p className="text-xs text-muted-foreground">
              Started: {startedStr}
            </p>
            {onReset && (
              <button
                type="button"
                onClick={onReset}
                className="text-xs text-muted-foreground underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              >
                Reset After Relapse
              </button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
